import { GIButton, Modal } from 'app-components';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import {
    NO_PAYMENT_VALUE,
    REASON_FOR_CANCEL_OPTIONS,
    TEXTAREA_MAX_LENGTH,
    TEXTAREA_MIN_LENGTH,
} from '@constants/enrollments/enrollments.constants';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { ModalContextType } from '@contexts/modalContext';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';

import { IEnrollment, IEnrollmentPremiumHistoryModal } from '../common/types';
import {
    StyledCharacterCounter,
    StyledDropdownContainer,
    StyledFooterContainer,
    StyledLabelNone,
    StyledTextAreaContainer,
} from './ModalStyles';
import { ModalTransactionError, ModalTransactionSuccess } from './ModalSuccessError';
import { MODAL_KEYS } from './modalKeys';

import useEnrollmentModal from '../hooks/useEnrollmentModal';

interface IModalProps extends ModalContextType {
    enrollment: IEnrollment;
    premiumHistory: IEnrollmentPremiumHistoryModal;
}

interface ICommentState {
    comment: string;
}

interface ISend834State {
    send_In_834: boolean;
}

interface ISendNoticeState {
    sendTermNotice: boolean;
}

const PremiumHistoryModal = ({
    closeModal,
    enrollment,
    premiumHistory: {
        handleSubmit,
        content: { title },
        successMessage,
        errorMessage,
        clearState,
    },
}: IModalProps) => {
    const { reloadEnrollmentDetails } = useEnrollmentModal();
    const intl = useIntl();
    const { id } = useParams<{ id: string }>();
    const { isSuccess, isError } = useSelector((state: any) => state?.memberEnrollment?.enrollmentRequest);
    const { sendTermFlag } = useSelector((state: any) => state?.memberEnrollment?.permissions);

    const [value, setValue] = useState<ICommentState>({ comment: '' });
    const [send834, setSend834] = useState<ISend834State>({ send_In_834: true });
    const [sendNotice, setSendNotice] = useState<ISendNoticeState>({ sendTermNotice: true });

    const maxChars = useMemo(() => TEXTAREA_MAX_LENGTH - value.comment.length, [value.comment]);

    //set 1st reason as default
    const defaultReason = REASON_FOR_CANCEL_OPTIONS[0].value;
    const [cancellationReasonCode, setCancellationReasonCode] = useState<string>(defaultReason);
    const isNoPayment = useMemo(() => cancellationReasonCode === NO_PAYMENT_VALUE, [cancellationReasonCode]);

    const handleClose = e => {
        closeModal(MODAL_KEYS.PREMIUM_HISTORY);
        if (isSuccess) {
            reloadEnrollmentDetails(enrollment.enrollmentId);
        }
        clearState();
        e.preventDefault();
    };

    const handleContinue = () => {
        handleSubmit({
            id: enrollment.enrollmentId,
            comment: value.comment,
            houseHoldId: id,
            send834: isNoPayment ? false : send834.send_In_834,
            sendTermNotice: isNoPayment ? false : sendNotice.sendTermNotice,
            cancellationReasonCode: cancellationReasonCode,
        });
    };

    return (
        <Modal id={MODAL_KEYS.PREMIUM_HISTORY} data-testid="premium-history-modal">
            <Modal.Header
                id="premiumHistoryModalTitle"
                titleId={title}
                onHide={handleClose}
                data-testid="modal-header"
            />
            <Modal.Body data-testid="modal-body">
                <ModalTransactionSuccess message={successMessage} isTrue={isSuccess} />
                <ModalTransactionError message={errorMessage} isTrue={isError} />

                <ConditionalRendering isTrue={!isSuccess && !isError}>
                    <>
                        <Form formName="premium-history-form" data-testid="premium-history-form">
                            <StyledDropdownContainer data-testid="dropdown-container">
                                <UniversalInput
                                    label={{
                                        text: intl.formatMessage({
                                            id: 'member.enrollment.modal.dropdown.reasonForCancel',
                                        }),
                                        isLabelRequired: false,
                                    }}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.DROPDOWN,
                                        name: 'cancellationReasonCode',
                                        value: cancellationReasonCode,
                                        options: REASON_FOR_CANCEL_OPTIONS,
                                        currentData: {
                                            cancellationReasonCode,
                                        },
                                        fields: [],
                                        isInputStandalone: true,
                                        inputClass: ['small-width'],
                                    }}
                                    inputActions={{
                                        onFieldChange: ({ cancellationReasonCode }) => {
                                            setCancellationReasonCode(cancellationReasonCode);
                                        },
                                    }}
                                    shouldParse
                                    data-testid="cancellation-reason-dropdown"
                                />
                            </StyledDropdownContainer>
                            <StyledLabelNone data-testid="label-none">
                                <StyledTextAreaContainer data-testid="textarea-container">
                                    <UniversalInput
                                        label={{
                                            text: intl.formatMessage({ id: 'member.enrollment.modal.textarea.label' }),
                                            labelClasses: ['spl__width--01'],
                                        }}
                                        inputData={{
                                            isInputRequired: true,
                                            type: INPUT_TYPES.TEXTAREA,
                                            name: 'comment',
                                            value: value.comment,
                                            liClass: ['text-left'],
                                            currentData: value,
                                            placeholder: intl.formatMessage({
                                                id: 'member.enrollment.modal.textarea.placeholder',
                                            }),
                                            rows: 8,
                                            validationType: VALIDATION_TYPES.LENGTH,
                                            valuesToMatch: [TEXTAREA_MIN_LENGTH, TEXTAREA_MAX_LENGTH],
                                        }}
                                        errorClasses={['margin-l-0']}
                                        inputActions={{
                                            onFieldChange: ({ overrideCommentText }) => {
                                                setValue(prev => ({ ...prev, overrideCommentText }));
                                            },
                                        }}
                                        data-testid="textarea-input"
                                    />
                                </StyledTextAreaContainer>
                            </StyledLabelNone>
                            <StyledCharacterCounter data-testid="character-counter">
                                {maxChars} {intl.formatMessage({ id: 'member.enrollment.modal.textLeft' })}
                            </StyledCharacterCounter>
                        </Form>
                        <ConditionalRendering isTrue={isNoPayment}>
                            <p>
                                <strong>
                                    {intl.formatMessage({ id: 'member.enrollment.modal.cancelPremiumHistory.note' })}{' '}
                                </strong>
                                {intl.formatMessage({
                                    id: 'member.enrollment.modal.cancelPremiumHistory.noteMessage',
                                })}
                            </p>
                        </ConditionalRendering>
                    </>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls data-testid="modal-controls">
                <ConditionalRendering isTrue={isSuccess || isError}>
                    <GIButton
                        id="button"
                        onClickHandler={handleClose}
                        variant="secondary"
                        labelId="button.ok"
                        data-testid="ok-button"
                    />
                </ConditionalRendering>

                <ConditionalRendering isTrue={!isSuccess && !isError}>
                    <StyledFooterContainer data-testid="footer-container">
                        <Form formName="send_In_834_form" data-testid="send-in-834-form">
                            <UniversalInput
                                label={{
                                    text: intl.formatMessage({ id: 'member.enrollment.modal.checkbox.send_In_834' }),
                                }}
                                inputData={{
                                    isInputRequired: false,
                                    type: INPUT_TYPES.CHECKBOX,
                                    name: 'send_In_834',
                                    classes: ['spl__width--01'],
                                    checked: send834.send_In_834,
                                    value: send834.send_In_834,
                                    currentData: send834,
                                    fields: [],
                                    disabled: isNoPayment,
                                }}
                                inputActions={{
                                    onFieldChange: ({ send_In_834 }) => {
                                        setSend834({ send_In_834 });
                                    },
                                }}
                                data-testid="send-in-834-checkbox"
                            />
                            {sendTermFlag && (
                                <UniversalInput
                                    label={{
                                        text: intl.formatMessage({
                                            id: 'member.enrollment.modal.premiumHistoryModal.sendTermNotice',
                                        }),
                                    }}
                                    inputData={{
                                        isInputRequired: false,
                                        type: INPUT_TYPES.CHECKBOX,
                                        name: 'sendTermNotice',
                                        classes: ['spl__width--01'],
                                        checked: sendNotice.sendTermNotice,
                                        value: sendNotice.sendTermNotice,
                                        currentData: sendNotice,
                                        fields: [],
                                    }}
                                    inputActions={{
                                        onFieldChange: ({ sendTermNotice }) => {
                                            setSendNotice({ sendTermNotice });
                                        },
                                    }}
                                    data-testid="send-term-notice-checkbox"
                                />
                            )}
                        </Form>
                        <GIButton
                            isDisabled={!value.comment.length}
                            id="button"
                            onClickHandler={handleContinue}
                            variant="primary"
                            labelId="button.submit"
                            data-testid="submit-button"
                        />
                        <GIButton
                            id="button"
                            onClickHandler={handleClose}
                            variant="secondary"
                            labelId="button.cancel"
                            data-testid="cancel-button"
                        />
                    </StyledFooterContainer>
                </ConditionalRendering>
            </Modal.Controls>
        </Modal>
    );
};

export default PremiumHistoryModal;
